import { Observable } from "rxjs/Observable"

import {
    INSIGHT_AREA_APPLY_FILTER_CRITERIA,
    INSIGHT_AREA_APPLY_MODEL_UPDATE,
    INSIGHT_AREA_CANCEL_MODEL_UPDATE,
    INSIGHT_AREA_ITEM_REQUEST,
    INSIGHT_AREA_ITEM_SYNC_REQUEST,
    INSIGHT_AREA_ITEM_RESPONSE,
    INSIGHT_AREA_ITEM_SAVE_RESPONSE,
    INSIGHT_AREA_LIST_PAGE_REQUEST,
    INSIGHT_AREA_LIST_RESPONSE,
    INSIGHT_AREA_LIST_SEARCH_REQUEST,
    INSIGHT_AREA_MAPPING_DELETE_PREVIEW_REQUEST,
    INSIGHT_AREA_MAPPING_PREVIEW_REQUEST,
    INSIGHT_AREA_REMOVE_MAPPING,
    INSIGHT_AREA_REMOVE_OWNER_VIEWER,
    INSIGHT_AREA_SEARCH_ADD_OWNERS_VIEWERS,
    INSIGHT_AREA_FINGERPRINT_PREVIEW_REQUEST,
    INSIGHT_AREA_FINGERPRINT_REMOVE_CONCEPT,
    INSIGHT_AREA_ADD_MAPPING,
} from "../actions/action-types"
import {
    attachmentItemDeleteErrorResponse,
    attachmentItemDeleteRequest,
    attachmentItemDeleteResponse,
    attachmentItemErrorResponse,
    attachmentItemSaveRequest,
    attachmentItemSaveResponse,
    attachmentListRequest,
} from "../actions/attachment-actions"
import { autocompleteOwnerViewerRequest } from "../actions/autocomplete-actions"
import { defaultSearchCriteria } from "../reducers/paging/topic-paging-reducer"
import {
    deleteAttachmentItem,
    postAttachmentItem,
    putAttachmentItem,
} from "../api/attachment-api"
import { getActionsToReplay } from "./default-epics/epic-utils"
import {
    insightAreaFinishSaving,
    insightAreaFinishSavingError,
    insightAreaItemRequest,
    insightAreaItemResponse,
    insightAreaItemSyncResponse,
    insightAreaItemSaveResponse,
    insightAreaListPageRequest,
    insightAreaMappingPreviewErrorResponse,
    insightAreaMappingPreviewResponse,
    insightAreaSearchAddOwnersViewersResponse,
    insightAreaUpdateModel,
    insightAreaFingerprintPreviewResponse,
    insightAreaFingerprintPreviewRequest,
    insightAreaMappingPreviewRequest,
} from "../actions/insight-area-actions"
import { addCustomInfoAlert } from "../actions/alert-actions"
import {
    postTopicPreviewMapping,
    deleteTopicPreviewMapping,
} from "../api/topic-api"
import {
    removeOwnerViewerEpic,
    requestItemDeleteHandler,
    requestItemSaveHandler,
    requestListFilterEpic,
    requestListSearchEpic,
    requestTopItemFromListResponseEpic,
    syncIdToURLEpic,
} from "./default-epics/default-entity-epics"
import {
    topicListRequest,
    topicItemRequest,
    topicItemSaveRequest,
    topicItemSyncRequest,
    topicFingerprintRequest,
    topicFingerprintPreviewRequest,
} from "../actions/topic-actions"
import {
    removeMappingEpic,
    requestMappingPreviewEpic,
} from "./default-epics/mapping-epics"
import { waitForAll } from "./default-epics/epic-utils"
import { idify } from "../reducers/paging/paging-reducer-utils"

export const requestInsightAreaSearchEpic = requestListSearchEpic(
    INSIGHT_AREA_LIST_SEARCH_REQUEST,
    insightAreaListPageRequest,
    "insightAreas",
    "topics"
)

export const applyInsightAreaFilterCriteria = requestListFilterEpic(
    INSIGHT_AREA_APPLY_FILTER_CRITERIA,
    insightAreaListPageRequest,
    "insightAreas",
    "topics"
)

export const requestInsightAreaPageEpic = (action$) =>
    action$
        .ofType(INSIGHT_AREA_LIST_PAGE_REQUEST)
        .map(({ type, ...pagingRequest }) => topicListRequest(pagingRequest))

export const requestInsightAreaItemEpic = (action$) =>
    action$
        .ofType(INSIGHT_AREA_ITEM_REQUEST)
        .map(({ id, isInvalidating }) =>
            topicItemRequest(id, isInvalidating, insightAreaItemResponse)
        )

/*
  Fetch additional data when the insight area is requested (attachments and fingerprint)
*/
export const requestAdditionalDataOnInsightAreaRequest = (
    action$,
    { getState }
) =>
    action$
        .ofType(INSIGHT_AREA_ITEM_REQUEST, INSIGHT_AREA_ITEM_SAVE_RESPONSE)
        .mergeMap((action) => {
            const user = getState().authentication.id
            const topicId = action.data ? action.data.id : action.id
            const isInvalidating =
                action.type === INSIGHT_AREA_ITEM_SAVE_RESPONSE
            const attachmentRequest = attachmentListRequest({
                searchCriteria: {
                    topic: topicId,
                    userid: user,
                },
                isInvalidating,
            })
            const fingerprintRequest = topicFingerprintRequest(
                topicId,
                isInvalidating
            )
            return Observable.of(attachmentRequest, fingerprintRequest)
        })

export const requestInsightAreaItemSyncEpic = (action$, { getState }) =>
    action$
        .ofType(INSIGHT_AREA_ITEM_SYNC_REQUEST)
        .mergeMap(() =>
            Observable.of(
                topicItemSyncRequest(
                    getState().presentation.insightAreas.activeId,
                    false,
                    insightAreaItemSyncResponse
                ),
                addCustomInfoAlert(
                    `Syncing ${
                        getState().presentation.insightAreas.workingModel.name
                    }, this may take up to 1 minute.`,
                    "Salesforce Sync"
                )
            )
        )

export const requestInsightAreaCancelModelUpdateEpic = (
    action$,
    { getState }
) =>
    action$
        .ofType(INSIGHT_AREA_CANCEL_MODEL_UPDATE)
        .map(() => getState().presentation.insightAreas.activeId)
        .filter((id) => id)
        .map(insightAreaItemRequest)

export const responseInsightAreaListEpic = requestTopItemFromListResponseEpic(
    INSIGHT_AREA_LIST_RESPONSE,
    insightAreaItemRequest,
    "insightAreas",
    "topics",
    defaultSearchCriteria
)

export const updateInsightAreaMappingConceptEpic = (action$, { getState }) =>
    action$
        .ofType(INSIGHT_AREA_ADD_MAPPING)
        .mergeMap(({ concept }) =>
            Observable.of(
                insightAreaFingerprintPreviewRequest(),
                insightAreaMappingPreviewRequest(
                    getState().presentation.insightAreas.activeId,
                    { positive: [concept] }
                )
            )
        )

export const removeInsightAreaMappingEpic = removeMappingEpic(
    INSIGHT_AREA_REMOVE_MAPPING,
    "insightAreas",
    null,
    insightAreaFingerprintPreviewRequest
)

export const requestInsightAreaMappingPreviewEpic = requestMappingPreviewEpic(
    [
        INSIGHT_AREA_MAPPING_PREVIEW_REQUEST,
        INSIGHT_AREA_MAPPING_DELETE_PREVIEW_REQUEST,
    ],
    postTopicPreviewMapping,
    deleteTopicPreviewMapping,
    insightAreaMappingPreviewResponse,
    insightAreaMappingPreviewErrorResponse
)

export const requestInsightAreaFingerprintPreviewEpic = (
    action$,
    { getState }
) =>
    action$.ofType(INSIGHT_AREA_FINGERPRINT_PREVIEW_REQUEST).map(() => {
        const {
            workingModel,
            fingerprintRemovedConcepts,
        } = getState().presentation.insightAreas
        const mappings = workingModel.mappings.reduce(
            (newMappings, mapping) => [...newMappings, ...mapping.positive],
            []
        )
        if (!mappings.length) {
            // Just return empty fp since we know it should be
            return insightAreaFingerprintPreviewResponse([])
        }
        const mappingIds = idify(mappings)
        return topicFingerprintPreviewRequest(
            mappingIds,
            fingerprintRemovedConcepts,
            workingModel.name,
            insightAreaFingerprintPreviewResponse
        )
    })

export const insightAreaSearchAddOwnersViewersEpic = (action$) =>
    action$
        .ofType(INSIGHT_AREA_SEARCH_ADD_OWNERS_VIEWERS)
        .map(({ searchTerm }) =>
            autocompleteOwnerViewerRequest(
                searchTerm,
                null,
                insightAreaSearchAddOwnersViewersResponse
            )
        )

export const insightAreaRemoveOwnerViewerEpic = removeOwnerViewerEpic(
    "insightAreas",
    INSIGHT_AREA_REMOVE_OWNER_VIEWER,
    insightAreaUpdateModel
)

export const syncInsightAreaIdToURLEpic = syncIdToURLEpic(
    "insight-areas",
    INSIGHT_AREA_ITEM_RESPONSE
)

export const requestInsightAreaApplyModelUpdateEpic = (action$, { getState }) =>
    action$.ofType(INSIGHT_AREA_APPLY_MODEL_UPDATE).map(() => {
        let {
            updateModel,
            activeId,
            imageToUpload,
        } = getState().presentation.insightAreas

        const placeholders_add = updateModel.get("placeholders_add")
        if (placeholders_add) {
            updateModel = updateModel.set(
                "placeholders_add",
                placeholders_add.map(({ image, attribution }) => ({
                    image,
                    attribution,
                }))
            )
        }

        return topicItemSaveRequest(
            activeId,
            updateModel.toJS(),
            imageToUpload,
            insightAreaItemSaveResponse
        )
    })

// Handles saving of one attachment (given a getState and action)
const attachmentSaveHandler = requestItemSaveHandler(
    putAttachmentItem,
    postAttachmentItem,
    "topicAttachments",
    attachmentItemSaveResponse,
    attachmentItemErrorResponse
)

// Handles deleting of one attachment (given a getState and action)
const attachmentDeleteHandler = requestItemDeleteHandler(
    deleteAttachmentItem,
    "attachment",
    attachmentItemDeleteResponse,
    attachmentItemDeleteErrorResponse
)

/*
  Handles attachment creation and deletion on insight area save.
  Emits ATTACHMENT_ITEM_SAVE_REQUEST, ATTACHMENT_ITEM_SAVE_RESPONSE,
  ATTACHMENT_ITEM_DELETE_REQUEST, ATTACHMENT_ITEM_DELETE_RESPONSE actions.
*/
export const handleAttachmentsOnInsightAreaSaveResponse = (
    action$,
    { getState }
) =>
    action$.ofType(INSIGHT_AREA_ITEM_SAVE_RESPONSE).mergeMap((action) => {
        const {
            mediaToAdd,
            mediaToDelete,
            workingModel,
        } = getState().presentation.insightAreas

        const saveActions = mediaToAdd.map((medium) =>
            attachmentItemSaveRequest(null, medium)
        )
        const deleteActions = mediaToDelete.map((medium) =>
            attachmentItemDeleteRequest(medium)
        )

        const allActions = [
            ...saveActions.map((action) =>
                attachmentSaveHandler(getState, action)
            ),
            ...deleteActions.map((action) =>
                attachmentDeleteHandler(getState, action)
            ),
        ]

        return waitForAll(allActions).mergeMap((responseActions) => {
            responseActions.forEach((action, index) => {
                if (action && action.type === "ATTACHMENT_ITEM_SAVE_RESPONSE") {
                    action.topic_id = saveActions[index].item.fields.topic_id
                }
            })

            const actionsToReplay = getActionsToReplay(
                responseActions,
                saveActions,
                deleteActions,
                insightAreaFinishSaving,
                insightAreaFinishSavingError,
                "insight area",
                workingModel.name
            )

            return Observable.of(...actionsToReplay)
        })
    })

export const requestFPPreviewOnRemovingFPConcept = (action$) =>
    action$
        .ofType(INSIGHT_AREA_FINGERPRINT_REMOVE_CONCEPT)
        .map(() => insightAreaFingerprintPreviewRequest())
